import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { fetchAirportData, fetchAirportServices } from '../services/airportDataService';
import { airportService } from '../services/airportService';

const AirportEditForm = ({ airport, onSave, onCancel, onDelete }) => {
  const [formData, setFormData] = useState({
    airportCode: '',
    airportName: '',
    city: '',
    state: '',
    elevation: '',
    atisNumber: '',
    awosNumber: '',
    dateModified: '',
    services: [],
    tags: [],
    operationalConsiderations: '',
    secretInformation: '',
    operationalLastModified: '',
    secretLastModified: '',
    dogsAndFun: '',
    dogsLastModified: '',
    groundTimeAndLogistics: '',
    logisticsLastModified: ''
  });
  const [tagInput, setTagInput] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [isServicesLoading, setIsServicesLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [servicesError, setServicesError] = useState('');
  const [progressStatus, setProgressStatus] = useState('');
  const [existingAirport, setExistingAirport] = useState(null);
  const isNewAirport = !airport?.id;

  useEffect(() => {
    if (airport) {
      setFormData({
        airportCode: airport.id || airport.airportCode || '',
        airportName: airport.airportName || '',
        city: airport.city || '',
        state: airport.state || '',
        elevation: airport.elevation || '',
        atisNumber: airport.atisNumber || '',
        awosNumber: airport.awosNumber || '',
        dateModified: airport.dateModified || new Date().toISOString(),
        services: airport.services || [],
        tags: airport.tags || [],
        operationalConsiderations: airport.operationalConsiderations || '',
        secretInformation: airport.secretInformation || '',
        operationalLastModified: airport.operationalLastModified || '',
        secretLastModified: airport.secretLastModified || '',
        dogsAndFun: airport.dogsAndFun || '',
        dogsLastModified: airport.dogsLastModified || '',
        groundTimeAndLogistics: airport.groundTimeAndLogistics || '',
        logisticsLastModified: airport.logisticsLastModified || ''
      });
    }
  }, [airport]);

  // Check if airport exists when code changes
  useEffect(() => {
    const checkAirportExists = async () => {
      if (formData.airportCode && isNewAirport) {
        try {
          const existingAirport = await airportService.getAirportById(formData.airportCode);
          setExistingAirport(existingAirport);
        } catch (error) {
          setExistingAirport(null);
        }
      } else {
        setExistingAirport(null);
      }
    };
    checkAirportExists();
  }, [formData.airportCode, isNewAirport]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'airportCode') {
      // Filter out non-alphanumeric characters and convert to uppercase
      const sanitizedValue = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
      setFormData(prev => ({
        ...prev,
        [name]: sanitizedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Tag management functions
  const handleTagInputChange = (e) => {
    setTagInput(e.target.value);
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const addTag = () => {
    // Clean the tag - remove any # symbols and normalize to lowercase
    const tag = tagInput.trim().toLowerCase().replace(/,/g, '').replace(/#/g, '');
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleAiAuto = async () => {
    if (!formData.airportCode) return;
    
    setIsServicesLoading(true);
    setIsAiLoading(true);
    setServicesError('');
    setAiError('');
    setProgressStatus('Starting AI Fill process...');

    try {
      // Run both fetches in parallel
      setProgressStatus('Fetching airport data from multiple sources...');
      const [servicesResult, airportDataResult] = await Promise.all([
        fetchAirportServices(formData.airportCode, (status) => {
          setProgressStatus(`FBO Services: ${status}`);
        }),
        fetchAirportData(formData.airportCode, (status) => {
          setProgressStatus(`Airport Data: ${status}`);
        })
      ]);

      setProgressStatus('Processing results...');
      // Update form with both results
      setFormData(prev => ({
        ...prev,
        ...airportDataResult,
        services: servicesResult
      }));

      setProgressStatus('AI Fill complete!');
      // Clear the status after a delay
      setTimeout(() => setProgressStatus(''), 3000);

    } catch (error) {
      console.error('Error in AI Fill:', error);
      const errorMessage = error.message || 'Failed to fetch data. Make sure you have editor permissions.';
      if (isServicesLoading) {
        setServicesError(errorMessage);
      }
      if (isAiLoading) {
        setAiError(errorMessage);
      }
      setProgressStatus('Error occurred during AI Fill');
    } finally {
      setIsServicesLoading(false);
      setIsAiLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full" id="airport-edit-form">
      <div className="bg-surface dark:bg-surface-dark rounded-lg shadow p-4">
        <div className="space-y-4">
          <div>
            <label htmlFor="airportCode" className="block text-sm font-medium text-primary dark:text-primary-dark">
              Airport Code
            </label>
            {isNewAirport ? (
              <div className="flex flex-col">
                <div className="flex items-center">
                  <input
                    type="text"
                    id="airportCode"
                    name="airportCode"
                    value={formData.airportCode}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-default dark:border-default-dark shadow-sm focus:ring-button focus:border-button dark:bg-surface-dark dark:text-primary-dark font-['Fira_Code'] text-2xl font-bold"
                    placeholder="KXYZ"
                  />
                  <div className="flex gap-2 ml-2 mt-1">
                    <button
                      type="button"
                      id="ai-auto"
                      name="ai-auto"
                      onClick={handleAiAuto}
                      disabled={isAiLoading || isServicesLoading || !formData.airportCode || existingAirport}
                      className={`px-2 py-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 dark:from-purple-500 dark:to-indigo-500 dark:hover:from-purple-600 dark:hover:to-indigo-600 text-white rounded-md flex items-center transition-all duration-200 shadow-sm hover:shadow group relative ${(isAiLoading || isServicesLoading || existingAirport) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      title="Use AI to auto-populate all airport information"
                    >
                      {(isAiLoading || isServicesLoading) ? (
                        <div className="flex items-center gap-2">
                          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-sm">Processing...</span>
                        </div>
                      ) : (
                        <span>Ai Fill</span>
                      )}
                    </button>
                  </div>
                </div>
                {existingAirport && (
                  <div className="mt-2 text-sm text-status-error dark:text-status-error-dark">
                    Airport {formData.airportCode} already exists. Please <a href={`/airport/${formData.airportCode}`} className="underline">Edit</a> or Delete the existing record.
                  </div>
                )}
                {progressStatus && (
                  <div className="mt-2 text-sm text-text-secondary dark:text-text-secondary-dark">
                    {progressStatus}
                  </div>
                )}
                {(aiError || servicesError) && (
                  <div className="mt-2 text-sm text-status-error dark:text-status-error-dark">
                    {aiError || servicesError}
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-1 font-['Fira_Code'] text-2xl font-bold text-primary dark:text-primary-dark">
                [{formData.airportCode}]
              </div>
            )}
          </div>

          <div>
            <label htmlFor="airportName" className="block text-sm font-medium text-primary dark:text-primary-dark">
              Airport Name
            </label>
            <input
              type="text"
              id="airportName"
              name="airportName"
              value={formData.airportName}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-default dark:border-default-dark shadow-sm focus:ring-button focus:border-button dark:bg-surface-dark dark:text-primary-dark"
            />
          </div>

          <div>
            <label htmlFor="city" className="block text-sm font-medium text-primary dark:text-primary-dark">
              City
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-default dark:border-default-dark shadow-sm focus:ring-button focus:border-button dark:bg-surface-dark dark:text-primary-dark"
            />
          </div>

          <div>
            <label htmlFor="state" className="block text-sm font-medium text-primary dark:text-primary-dark">
              State
            </label>
            <input
              type="text"
              id="state"
              name="state"
              value={formData.state}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-default dark:border-default-dark shadow-sm focus:ring-button focus:border-button dark:bg-surface-dark dark:text-primary-dark"
            />
          </div>

          <div>
            <label htmlFor="elevation" className="block text-sm font-medium text-primary dark:text-primary-dark">
              Elevation (ft)
            </label>
            <input
              type="text"
              id="elevation"
              name="elevation"
              value={formData.elevation}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-default dark:border-default-dark shadow-sm focus:ring-button focus:border-button dark:bg-surface-dark dark:text-primary-dark"
            />
          </div>

          <div className="flex space-x-4">
            <div className="w-1/2">
              <label htmlFor="atisNumber" className="block text-sm font-medium text-primary dark:text-primary-dark">
                ATIS Number
              </label>
              <input
                type="text"
                id="atisNumber"
                name="atisNumber"
                value={formData.atisNumber}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-default dark:border-default-dark shadow-sm focus:ring-button focus:border-button dark:bg-surface-dark dark:text-primary-dark whitespace-nowrap"
              />
            </div>

            <div className="w-1/2">
              <label htmlFor="awosNumber" className="block text-sm font-medium text-primary dark:text-primary-dark">
                AWOS Number
              </label>
              <input
                type="text"
                id="awosNumber"
                name="awosNumber"
                value={formData.awosNumber}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-default dark:border-default-dark shadow-sm focus:ring-button focus:border-button dark:bg-surface-dark dark:text-primary-dark whitespace-nowrap"
              />
            </div>
          </div>

          {/* Tags section */}
          <div>
            <label htmlFor="tags" className="block text-sm font-medium text-primary dark:text-primary-dark">
              Tags
            </label>
            <div className="mt-1">
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.tags.map((tag, index) => (
                  <span 
                    key={index} 
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-button/20 text-button dark:bg-button-dark/30 dark:text-button-dark"
                  >
                    #{tag.replace(/#/g, '')}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-button/30 dark:hover:bg-button-dark/40"
                    >
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex">
                <input
                  type="text"
                  id="tag-input"
                  value={tagInput}
                  onChange={handleTagInputChange}
                  onKeyDown={handleTagInputKeyDown}
                  onBlur={addTag}
                  placeholder="Add tags (e.g. region2, specialuse, nightrestricted)"
                  className="block w-full rounded-md border-default dark:border-default-dark shadow-sm focus:ring-button focus:border-button dark:bg-surface-dark dark:text-primary-dark"
                />
                <button
                  type="button"
                  onClick={addTag}
                  className="ml-2 px-3 py-2 bg-button text-white rounded-md hover:bg-button-hover dark:bg-button-dark dark:hover:bg-button-hover-dark"
                >
                  Add
                </button>
              </div>
              <p className="mt-1 text-xs text-text-secondary dark:text-text-secondary-dark">
                Press Enter or comma to add a tag. Tags can be used for filtering (e.g. #region1)
              </p>
            </div>
          </div>

          <div className="pt-4 mt-4 border-t border-default dark:border-default-dark">
            {showDeleteConfirm ? (
              <div className="mb-12">
                <div className="p-4 border rounded-md bg-status-error-bg border-status-error text-black">
                  <p className="mb-4">Are you sure you want to delete this airport? This action cannot be undone.</p>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      id="cancel-delete"
                      name="cancel-delete"
                      onClick={() => setShowDeleteConfirm(false)}
                      className="px-4 py-2 bg-surface text-black rounded-md hover:bg-surface-hover"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      id="confirm-delete"
                      name="confirm-delete"
                      onClick={async () => {
                        try {
                          await onDelete();
                        } catch (error) {
                          console.error('Error deleting airport:', error);
                          return;
                        }
                        setShowDeleteConfirm(false);
                      }}
                      className="px-4 py-2 bg-status-error-text text-white rounded-md hover:bg-status-error-text/90"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex justify-between gap-2">
                {!isNewAirport && (
                  <button
                    type="button"
                    id="delete-airport"
                    name="delete-airport"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="px-4 py-2 text-red-600 hover:text-red-700 dark:text-red-300 dark:hover:text-red-200"
                  >
                    Delete Airport
                  </button>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    id="cancel-edit"
                    name="cancel-edit"
                    onClick={onCancel}
                    className="px-4 py-2 text-secondary hover:text-secondary-hover dark:text-secondary-dark dark:hover:text-secondary-hover-dark"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    id="save-airport"
                    name="save-airport"
                    disabled={existingAirport}
                    className={`px-4 py-2 bg-button text-white rounded-md hover:bg-button-hover dark:bg-button-dark dark:hover:bg-button-hover-dark ${existingAirport ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Save
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="h-[100px]"></div>
    </form>
  );
};

AirportEditForm.propTypes = {
  airport: PropTypes.shape({
    airportCode: PropTypes.string,
    airportName: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    elevation: PropTypes.string,
    atisNumber: PropTypes.string,
    awosNumber: PropTypes.string,
    dateModified: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
  }),
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default AirportEditForm; 